/**
 * Mock ITSM corpus for Wecrew Copilot AI — incidents, changes, CMDB.
 * IDs align with mockObservability SRE log incident_id fields.
 */

export const ITSM_USERS = [
  { id: "u1", name: "Rajkumar Madhu", role: "Incident Commander", team: "SRE Platform" },
  { id: "u2", name: "Arun Venkat", role: "Platform Engineer", team: "Platform" },
  { id: "u3", name: "Priya Sundaram", role: "Responder", team: "Application" },
  { id: "u4", name: "Karthik Raja", role: "DBA", team: "Database" },
];

export const MOCK_INCIDENTS = [
  {
    id: "INC-2841",
    priority: "P1",
    severity: 1,
    title: "Connection pool exhausted — argusai-api 503 cascade",
    service: "argusai-api",
    assignee: "u1",
    status: "active",
    age: "14m",
    slaPct: 32,
    slaState: "breach",
    sloBurn: 4.2,
    impacted: "12.4k chat queries / hr",
    category: "Availability",
    source: "Observability · SRE",
    opened: "2026-01-20 17:26",
    updated: "2m ago",
    description:
      "Error budget burn rate 4.2× after deploy v3.0.1. Pool max 100 reached; PagerDuty bridge opened. Correlated with PostgreSQL connection saturation.",
    timeline: [
      { t: "17:26:08", type: "critical", body: "PagerDuty escalation — Sev 1 declared (pool_timeout_rate > 5%)" },
      { t: "17:26:30", type: "ai", body: "Wecrew Copilot AI correlated DB CPU spike, GC pauses, and Stripe webhook rate limits — likely pool exhaustion." },
      { t: "17:27:02", type: "info", body: "Rajkumar Madhu joined as Incident Commander" },
      { t: "17:28:47", type: "warn", body: "Auto-scaled argusai-api 8 → 16 replicas via runbook pool-expansion-v2" },
      { t: "17:31:09", type: "success", body: "p95 latency dropped 8.4s → 620ms after rollback candidate identified" },
    ],
    relatedLogs: { domain: "sre", query: "incident_id:INC-2841" },
  },
  {
    id: "INC-2840",
    priority: "P2",
    severity: 2,
    title: "Deploy correlation — errors spiked after argusai-api v3.0.1",
    service: "argusai-api",
    assignee: "u2",
    status: "investigating",
    age: "42m",
    slaPct: 68,
    slaState: "warning",
    sloBurn: 1.8,
    impacted: "DevOps pipeline · prod",
    category: "Change-related",
    source: "Observability · DevOps",
    opened: "2026-01-20 16:58",
    updated: "8m ago",
    description: "SRE deploy correlator flagged error spike 12 minutes post-release. Change CHG-1042 linked.",
    timeline: [
      { t: "16:58:12", type: "info", body: "Incident opened from deploy correlation alert" },
      { t: "17:02:44", type: "info", body: "Linked to change CHG-1042 (pool config reduction)" },
    ],
    relatedLogs: { domain: "devops", query: "tag:deploy AND service:argusai-api" },
  },
  {
    id: "INC-2839",
    priority: "P2",
    severity: 2,
    title: "Gateway availability SLO below 99.5% target",
    service: "argusai-gateway",
    assignee: "u3",
    status: "mitigating",
    age: "1h 12m",
    slaPct: 71,
    slaState: "warning",
    sloBurn: 2.1,
    impacted: "API ingress · Mumbai region",
    category: "Performance",
    source: "Observability · SRE",
    opened: "2026-01-20 16:28",
    updated: "15m ago",
    description: "30-day availability at 99.2%. Elevated 502 from upstream argusai-api during peak.",
    timeline: [
      { t: "16:28:00", type: "warn", body: "SLO tracker breach warning — gateway availability" },
      { t: "16:45:22", type: "info", body: "Mitigation: increased gateway timeout to 30s" },
    ],
    relatedLogs: { domain: "sre", query: "incident_id:INC-2839" },
  },
  {
    id: "INC-2838",
    priority: "P3",
    severity: 3,
    title: "K3s scheduler API errors on wecrew-worker-02",
    service: "k3s-control",
    assignee: "u2",
    status: "open",
    age: "3h 18m",
    slaPct: 92,
    slaState: "ok",
    sloBurn: 0.4,
    impacted: "Platform · scheduling",
    category: "Platform",
    source: "Observability · Platform",
    opened: "2026-01-20 14:22",
    updated: "1h ago",
    description: "Intermittent 500 from kube-scheduler. Pod scheduling delayed in platform namespace.",
    timeline: [{ t: "14:22:10", type: "info", body: "Opened from platform log alert — level:ERROR tag:k3s" }],
    relatedLogs: { domain: "platform", query: "tag:k3s AND level:ERROR" },
  },
  {
    id: "INC-2835",
    priority: "P4",
    severity: 4,
    title: "Certificate expiring in 7 days — api.wecrew.in",
    service: "edge",
    assignee: null,
    status: "open",
    age: "12h",
    slaPct: 95,
    slaState: "ok",
    sloBurn: 0,
    impacted: "Proactive",
    category: "Security",
    source: "Synthetic monitor",
    opened: "2026-01-20 05:00",
    updated: "4h ago",
    description: "TLS cert for api.wecrew.in expires 2026-01-27. Renewal change pending CAB.",
    timeline: [],
    relatedLogs: null,
  },
  {
    id: "INC-2832",
    priority: "P3",
    severity: 3,
    title: "Vector index queue backlog exceeding threshold",
    service: "argusai-indexer",
    assignee: "u4",
    status: "resolved",
    age: "2d",
    slaPct: 100,
    slaState: "ok",
    sloBurn: 0,
    impacted: "Document ingestion delayed ~20m",
    category: "Capacity",
    source: "Agent runtime",
    opened: "2026-01-18 09:14",
    updated: "1d ago",
    description: "Resolved after scaling indexer workers 2 → 6.",
    timeline: [{ t: "09:14:00", type: "info", body: "Opened" }, { t: "11:02:00", type: "success", body: "Resolved — queue drained" }],
    relatedLogs: { domain: "application", query: "service:argusai-indexer" },
  },
];

export const MOCK_CHANGES = [
  {
    id: "CHG-1042",
    title: "Reduce PostgreSQL pool size on argusai-api (100 → 80)",
    type: "Normal",
    state: "Implemented",
    risk: "Moderate",
    service: "argusai-api",
    assignee: "u2",
    windowStart: "2026-01-20 16:00",
    windowEnd: "2026-01-20 17:00",
    lane: "Application",
    calendarPct: 12,
    calendarWidth: 18,
    linkedIncident: "INC-2840",
    approved: true,
  },
  {
    id: "CHG-1043",
    title: "Emergency rollback — restore pool max 100 connections",
    type: "Emergency",
    state: "In Progress",
    risk: "Low",
    service: "argusai-api",
    assignee: "u1",
    windowStart: "2026-01-20 17:30",
    windowEnd: "2026-01-20 18:00",
    lane: "Application",
    calendarPct: 68,
    calendarWidth: 14,
    linkedIncident: "INC-2841",
    approved: true,
  },
  {
    id: "CHG-1038",
    title: "Upgrade K3s control plane patch 1.29.4 → 1.29.5",
    type: "Normal",
    state: "CAB Review",
    risk: "High",
    service: "k3s-control",
    assignee: "u2",
    windowStart: "2026-01-21 02:00",
    windowEnd: "2026-01-21 05:00",
    lane: "Platform",
    calendarPct: 8,
    calendarWidth: 28,
    linkedIncident: null,
    approved: false,
    runbookId: "upgrade-k3s",
  },
  {
    id: "CHG-1044",
    title: "PostgreSQL major upgrade 15.4 → 16.2",
    type: "Normal",
    state: "Scheduled",
    risk: "High",
    service: "postgresql-primary",
    assignee: "u4",
    windowStart: "2026-01-25 02:00",
    windowEnd: "2026-01-25 05:00",
    lane: "Platform",
    calendarPct: 55,
    calendarWidth: 24,
    linkedIncident: null,
    approved: true,
    runbookId: "upgrade-postgres",
  },
  {
    id: "CHG-1045",
    title: "Rolling deploy argusai-api v3.0.0 → v3.1.0",
    type: "Standard",
    state: "Approved",
    risk: "Moderate",
    service: "argusai-api",
    assignee: "u3",
    windowStart: "2026-01-22 14:00",
    windowEnd: "2026-01-22 15:00",
    lane: "Application",
    calendarPct: 38,
    calendarWidth: 16,
    linkedIncident: null,
    approved: true,
    runbookId: "upgrade-argusai-api",
  },
  {
    id: "CHG-1046",
    title: "Upgrade Flowise 1.8.2 → 1.8.4 & n8n 1.22 → 1.24",
    type: "Normal",
    state: "Scheduled",
    risk: "Moderate",
    service: "flowise",
    assignee: "u2",
    windowStart: "2026-01-23 03:00",
    windowEnd: "2026-01-23 04:30",
    lane: "Platform",
    calendarPct: 62,
    calendarWidth: 18,
    linkedIncident: null,
    approved: true,
    runbookId: "upgrade-flowise",
  },
  {
    id: "CHG-1035",
    title: "Rotate Infisical secrets for argusai-api DB credentials",
    type: "Standard",
    state: "Scheduled",
    risk: "Low",
    service: "argusai-api",
    assignee: "u4",
    windowStart: "2026-01-22 03:00",
    windowEnd: "2026-01-22 04:00",
    lane: "Security",
    calendarPct: 22,
    calendarWidth: 12,
    linkedIncident: null,
    approved: true,
  },
  {
    id: "CHG-1031",
    title: "Deploy Cilium network policy tightening — platform ns",
    type: "Normal",
    state: "Approved",
    risk: "Moderate",
    service: "cilium",
    assignee: "u2",
    windowStart: "2026-01-21 14:00",
    windowEnd: "2026-01-21 16:00",
    lane: "Platform",
    calendarPct: 45,
    calendarWidth: 22,
    linkedIncident: null,
    approved: true,
  },
];

export const MOCK_CMDB = [
  {
    id: "CI-001",
    name: "argusai-api",
    cls: "Application",
    tier: "Tier-1",
    owner: "Platform Eng",
    status: "degraded",
    uptime: 98.4,
    deps: 4,
    env: "production",
    region: "ap-south-1",
    incidents: 2,
  },
  {
    id: "CI-002",
    name: "argusai-gateway",
    cls: "Application",
    tier: "Tier-1",
    owner: "Platform Eng",
    status: "degraded",
    uptime: 99.1,
    deps: 3,
    env: "production",
    region: "ap-south-1",
    incidents: 1,
  },
  {
    id: "CI-003",
    name: "postgresql-primary",
    cls: "Database",
    tier: "Tier-1",
    owner: "Database",
    status: "healthy",
    uptime: 99.95,
    deps: 0,
    env: "production",
    region: "ap-south-1",
    incidents: 0,
  },
  {
    id: "CI-004",
    name: "argusai-indexer",
    cls: "Application",
    tier: "Tier-2",
    owner: "AI Engine",
    status: "healthy",
    uptime: 99.8,
    deps: 2,
    env: "production",
    region: "ap-south-1",
    incidents: 0,
  },
  {
    id: "CI-005",
    name: "k3s-control",
    cls: "Infrastructure",
    tier: "Tier-1",
    owner: "Platform Eng",
    status: "degraded",
    uptime: 97.2,
    deps: 0,
    env: "production",
    region: "ap-south-1",
    incidents: 1,
  },
  {
    id: "CI-006",
    name: "cilium-agent",
    cls: "Infrastructure",
    tier: "Tier-2",
    owner: "Platform Eng",
    status: "healthy",
    uptime: 99.99,
    deps: 1,
    env: "production",
    region: "ap-south-1",
    incidents: 0,
  },
  {
    id: "CI-007",
    name: "vector-store",
    cls: "Database",
    tier: "Tier-2",
    owner: "AI Engine",
    status: "healthy",
    uptime: 99.9,
    deps: 0,
    env: "production",
    region: "ap-south-1",
    incidents: 0,
  },
  {
    id: "CI-008",
    name: "edge-ingress",
    cls: "Network",
    tier: "Tier-1",
    owner: "Platform Eng",
    status: "healthy",
    uptime: 100,
    deps: 2,
    env: "production",
    region: "ap-south-1",
    incidents: 0,
  },
];

export const CMDB_CLASSES = [
  { key: "Application", label: "Applications", count: 4, icon: "📦" },
  { key: "Database", label: "Databases", count: 2, icon: "🗄️" },
  { key: "Infrastructure", label: "Infrastructure", count: 2, icon: "☸️" },
  { key: "Network", label: "Network", count: 1, icon: "🌐" },
];

const INC_OVERRIDES_KEY = "wecrew-itsm-inc-overrides";
const CHG_OVERRIDES_KEY = "wecrew-itsm-chg-overrides";

export const INCIDENT_STATUSES = [
  "active",
  "investigating",
  "mitigating",
  "open",
  "resolved",
  "closed",
];

export const CHANGE_STATES = [
  "Scheduled",
  "Approved",
  "CAB Review",
  "In Progress",
  "Implemented",
];

function readOverrides(key) {
  if (typeof sessionStorage === "undefined") return {};
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeOverrides(key, obj) {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.setItem(key, JSON.stringify(obj));
  } catch {
    /* quota / private mode */
  }
}

/** Incidents with demo session edits applied (browser session only). */
export function listIncidents() {
  const overrides = readOverrides(INC_OVERRIDES_KEY);
  return MOCK_INCIDENTS.map((row) => {
    const patch = overrides[row.id];
    return patch ? { ...row, ...patch } : row;
  });
}

export function patchIncident(id, patch) {
  const overrides = readOverrides(INC_OVERRIDES_KEY);
  overrides[id] = { ...(overrides[id] || {}), ...patch, updated: "just now" };
  writeOverrides(INC_OVERRIDES_KEY, overrides);
}

/** Changes with demo session edits applied. */
export function listChanges() {
  const overrides = readOverrides(CHG_OVERRIDES_KEY);
  return MOCK_CHANGES.map((row) => {
    const patch = overrides[row.id];
    return patch ? { ...row, ...patch } : row;
  });
}

export function patchChange(id, patch) {
  const overrides = readOverrides(CHG_OVERRIDES_KEY);
  overrides[id] = { ...(overrides[id] || {}), ...patch };
  writeOverrides(CHG_OVERRIDES_KEY, overrides);
}

export function getIncidentById(id) {
  return listIncidents().find((i) => i.id === id) ?? null;
}

export function getChangeById(id) {
  return listChanges().find((c) => c.id === id) ?? null;
}

export function getUserById(id) {
  return ITSM_USERS.find((u) => u.id === id) ?? null;
}

export function countOpenIncidents() {
  return listIncidents().filter((i) => !["resolved", "closed"].includes(i.status)).length;
}

export function priorityTone(priority) {
  if (priority === "P1") return "red";
  if (priority === "P2") return "amber";
  if (priority === "P3") return "blue";
  return "teal";
}

export function slaTone(state) {
  if (state === "breach") return "red";
  if (state === "warning") return "amber";
  return "green";
}

export function statusLabel(status) {
  const map = {
    active: "Active",
    investigating: "Investigating",
    mitigating: "Mitigating",
    open: "Open",
    resolved: "Resolved",
    closed: "Closed",
  };
  return map[status] || status;
}
